import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  inviteOrganizationMember,
  listDeviceSessions,
  listOrganizationMembers,
  revokeDeviceSession,
  updateOrganizationMember,
} from '../../api/billing'
import { formatApiErrorMessage } from '../../api/apiErrorMessage'
import { isSeatLimitError, parseBillingLimitError, seatLimitMessage } from '../../api/apiErrorCodes'
import type { DeviceSessionDto, OrganizationDto, OrganizationMemberDto } from '../../api/types'
import { getOrCreateDeviceId } from '../../auth/deviceId'
import { useOrganizationAccess } from '../../hooks/useOrganizationAccess'
import { PaywallModal } from './PaywallModal'
import { SelectCombobox } from '../SelectCombobox'

const JOB_TITLE_PRESETS = [
  { value: 'manager', label: 'Управляющий' },
  { value: 'purchaser', label: 'Закупщик' },
  { value: 'chef', label: 'Шеф-повар' },
] as const

/** Раздел биллинг-панели: команда / устройства (тариф — только в «Платежи»). */
export type BillingSettingsSection = 'team' | 'devices'

type BillingSettingsPanelProps = {
  organization: OrganizationDto | undefined
  section: BillingSettingsSection
}

export const BillingSettingsPanel = ({ organization, section }: BillingSettingsPanelProps) => {
  const navigate = useNavigate()
  const { isOwner, requestOwnerAccess, ownerPaywallModal } = useOrganizationAccess()
  const [sessions, setSessions] = useState<DeviceSessionDto[]>([])
  const [members, setMembers] = useState<OrganizationMemberDto[]>([])
  const [inviteEmail, setInviteEmail] = useState('')
  const [invitePreset, setInvitePreset] = useState<(typeof JOB_TITLE_PRESETS)[number]['value']>('purchaser')
  const [inviteVenueIds, setInviteVenueIds] = useState<number[]>([])
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [seatLimitPaywall, setSeatLimitPaywall] = useState<{ message: string; planName: string } | null>(null)
  const [sessionsExpanded, setSessionsExpanded] = useState(false)
  const [editingMemberId, setEditingMemberId] = useState<number | null>(null)
  const [editingTitle, setEditingTitle] = useState('')
  const [editingVenueIds, setEditingVenueIds] = useState<number[]>([])
  const currentDeviceId = getOrCreateDeviceId()
  const venues = organization?.venues ?? []

  useEffect(() => {
    if (venues.length === 1) {
      setInviteVenueIds([venues[0].id])
    }
  }, [venues])

  const reload = useCallback(async () => {
    if (!organization?.id || !isOwner) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      if (section === 'devices') {
        setSessions(await listDeviceSessions())
      } else {
        setMembers(await listOrganizationMembers(organization.id))
      }
    } catch (loadError) {
      setError(formatApiErrorMessage(loadError, 'Не удалось загрузить данные'))
    } finally {
      setLoading(false)
    }
  }, [isOwner, organization?.id, section])

  useEffect(() => {
    void reload()
  }, [reload])

  const handleRevoke = async (sessionId: number) => {
    if (!requestOwnerAccess()) {
      return
    }
    setError(null)
    setMessage(null)
    try {
      await revokeDeviceSession(sessionId)
      setMessage('Сессия завершена')
      await reload()
    } catch (revokeError) {
      setError(formatApiErrorMessage(revokeError, 'Не удалось завершить сессию'))
    }
  }

  const toggleInviteVenue = (venueId: number) => {
    setInviteVenueIds((current) =>
      current.includes(venueId) ? current.filter((id) => id !== venueId) : [...current, venueId],
    )
  }

  const toggleEditingVenue = (venueId: number) => {
    setEditingVenueIds((current) =>
      current.includes(venueId) ? current.filter((id) => id !== venueId) : [...current, venueId],
    )
  }

  const venueNames = (venueIds: number[] | undefined) => {
    if (venueIds == null || venueIds.length === 0) {
      return 'все заведения'
    }
    const names = venueIds
      .map((id) => venues.find((venue) => venue.id === id)?.name)
      .filter((name): name is string => Boolean(name))
    return names.length > 0 ? names.join(', ') : 'заведения не назначены'
  }

  const handleInvite = async (event: FormEvent) => {
    event.preventDefault()
    if (!organization?.id || !inviteEmail.trim() || !requestOwnerAccess()) {
      return
    }
    if (venues.length > 0 && inviteVenueIds.length === 0) {
      setError('Выберите хотя бы одно заведение для сотрудника')
      return
    }
    setError(null)
    setMessage(null)
    try {
      await inviteOrganizationMember(organization.id, {
        email: inviteEmail.trim(),
        job_title_preset: invitePreset,
        ...(inviteVenueIds.length > 0 ? { venue_ids: inviteVenueIds } : {}),
      })
      setInviteEmail('')
      setInviteVenueIds(venues.length === 1 ? [venues[0].id] : [])
      setMessage('Участник приглашён')
      await reload()
    } catch (inviteError) {
      if (isSeatLimitError(inviteError)) {
        const details = parseBillingLimitError(inviteError)
        setSeatLimitPaywall({
          message: seatLimitMessage(inviteError),
          planName: details?.requiredPlanDisplayName ?? 'Стандарт',
        })
        return
      }
      setError(formatApiErrorMessage(inviteError, 'Не удалось пригласить участника'))
    }
  }

  const startEditMember = (member: OrganizationMemberDto) => {
    if (!requestOwnerAccess()) {
      return
    }
    setError(null)
    setMessage(null)
    setEditingMemberId(member.id)
    setEditingTitle(member.job_title ?? '')
    setEditingVenueIds(member.venue_ids ?? [])
  }

  const cancelEditMember = () => {
    setEditingMemberId(null)
    setEditingTitle('')
    setEditingVenueIds([])
  }

  const saveMemberTitle = async (member: OrganizationMemberDto) => {
    if (!organization?.id) {
      return
    }
    if (venues.length > 0 && editingVenueIds.length === 0) {
      setError('У сотрудника должно быть хотя бы одно заведение')
      return
    }
    try {
      await updateOrganizationMember(organization.id, member.id, {
        job_title: editingTitle.trim(),
        ...(venues.length > 0 ? { venue_ids: editingVenueIds } : {}),
      })
      setMessage('Карточка участника обновлена')
      cancelEditMember()
      await reload()
    } catch (updateError) {
      setError(formatApiErrorMessage(updateError, 'Не удалось обновить участника'))
    }
  }

  if (!organization || organization.org_type === 'supplier') {
    return null
  }

  const currentSession = sessions.find((row) => row.device_id === currentDeviceId)
  const otherSessionsCount = sessions.length - (currentSession ? 1 : 0)

  const feedback = (
    <>
      {loading ? <p className="market-muted">Загрузка…</p> : null}
      {message ? <p className="market-success">{message}</p> : null}
      {error ? <p className="echat-error">{error}</p> : null}
    </>
  )

  if (section === 'devices') {
    return (
      <div className="market-billing-section">
        {feedback}
        {!isOwner ? (
          <p className="market-muted">Управление сессиями доступно владельцу аккаунта.</p>
        ) : sessions.length === 0 ? (
          <p className="market-muted">Нет активных сессий</p>
        ) : (
          <div className="market-billing-sessions">
            {currentSession ? (
              <p className="market-billing-sessions__preview">
                Текущее: <strong>{currentSession.device_label ?? 'Устройство'}</strong>
                {otherSessionsCount > 0
                  ? ` · ещё ${otherSessionsCount} ${otherSessionsCount === 1 ? 'устройство' : otherSessionsCount < 5 ? 'устройства' : 'устройств'}`
                  : null}
              </p>
            ) : null}

            {sessions.length > 1 ? (
              <>
                <button
                  type="button"
                  className="echat-archive__toggle market-billing-sessions__toggle"
                  aria-expanded={sessionsExpanded}
                  onClick={() => setSessionsExpanded((expanded) => !expanded)}
                >
                  <span>{sessionsExpanded ? 'Скрыть устройства' : 'Показать все устройства'}</span>
                  <span className="echat-archive__count">{sessions.length}</span>
                  <span className="echat-archive__chevron" aria-hidden="true">
                    {sessionsExpanded ? '▾' : '▸'}
                  </span>
                </button>

                {sessionsExpanded ? (
                  <ul className="market-billing-list market-billing-list--compact">
                    {sessions.map((row) => (
                      <li key={row.id} className="market-billing-list__item">
                        <div>
                          <strong>{row.device_label ?? 'Устройство'}</strong>
                          {row.device_id === currentDeviceId ? (
                            <span className="market-billing-list__tag">текущее</span>
                          ) : null}
                          <p className="market-muted">
                            Последняя активность: {new Date(row.last_seen_at).toLocaleString('ru-RU')}
                          </p>
                        </div>
                        {row.device_id !== currentDeviceId ? (
                          <button
                            type="button"
                            className="echat-btn echat-btn--ghost echat-btn--sm"
                            onClick={() => void handleRevoke(row.id)}
                          >
                            Завершить
                          </button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </>
            ) : (
              <ul className="market-billing-list market-billing-list--compact">
                {sessions.map((row) => (
                  <li key={row.id} className="market-billing-list__item">
                    <div>
                      <strong>{row.device_label ?? 'Устройство'}</strong>
                      <span className="market-billing-list__tag">текущее</span>
                      <p className="market-muted">
                        Последняя активность: {new Date(row.last_seen_at).toLocaleString('ru-RU')}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {ownerPaywallModal}
      </div>
    )
  }

  // section === 'team'
  const seatsUsed = organization.seats_used ?? 0
  const seatsLimit = organization.seats_limit ?? 0
  const seatsAvailable = Math.max(0, seatsLimit - seatsUsed)
  const venuesUsed = organization.venues_used ?? organization.venues?.length ?? 0
  const venuesLimit = organization.venues_limit ?? Math.max(venuesUsed, 1)
  const venuesAvailable = Math.max(0, venuesLimit - venuesUsed)

  return (
    <div className="market-billing-section">
      {feedback}
      {seatsLimit > 0 || venuesLimit > 0 ? (
        <p className="echat-muted market-billing-section__limits">
          Можно добавить: сотрудников — {seatsAvailable}
          {seatsLimit > 0 ? ` (занято ${seatsUsed} из ${seatsLimit})` : ''}
          {' · '}
          заведений — {venuesAvailable}
          {venuesLimit > 0 ? ` (занято ${venuesUsed} из ${venuesLimit})` : ''}
          {' · '}
          до 2 сотрудников на заведение
        </p>
      ) : null}
      {!isOwner ? (
        <p className="market-muted">Приглашение и управление командой доступны владельцу аккаунта.</p>
      ) : (
        <>
          <div className="echat-inbox market-settings-inbox">
            <ul className="echat-inbox__list">
              {members.map((member) => (
                <li key={member.id} className="echat-inbox__item market-settings-inbox__item">
                  <div className="echat-inbox__row market-settings-inbox__row">
                    <span className="echat-inbox__body">
                      <span className="echat-inbox__name">
                        {member.display_name ?? member.email ?? 'Участник'}
                      </span>
                      <span className="echat-inbox__preview echat-muted">
                        {member.email ?? '—'} · {member.job_title ?? member.member_role}
                        {member.member_role === 'employee'
                          ? ` · ${venueNames(member.venue_ids)}`
                          : ''}
                      </span>
                    </span>
                    {member.member_role === 'employee' && editingMemberId !== member.id ? (
                      <button
                        type="button"
                        className="echat-btn echat-btn--ghost echat-btn--sm"
                        onClick={() => startEditMember(member)}
                      >
                        Изменить
                      </button>
                    ) : null}
                  </div>
                  {editingMemberId === member.id ? (
                    <form
                      className="market-settings-member-edit"
                      onSubmit={(event) => {
                        event.preventDefault()
                        void saveMemberTitle(member)
                      }}
                    >
                      <div className="market-settings-invite__row">
                        <label className="echat-field market-settings-invite__field">
                          <span className="echat-field__label">Название в команде</span>
                          <input
                            className="echat-field__input"
                            type="text"
                            autoFocus
                            value={editingTitle}
                            onChange={(event) => setEditingTitle(event.target.value)}
                            placeholder="Например, Шеф-повар"
                          />
                        </label>
                        <button type="submit" className="echat-btn echat-btn--primary echat-btn--sm">
                          Сохранить
                        </button>
                        <button
                          type="button"
                          className="echat-btn echat-btn--ghost echat-btn--sm"
                          onClick={cancelEditMember}
                        >
                          Отмена
                        </button>
                      </div>
                      {venues.length > 0 ? (
                        <fieldset className="market-fieldset market-settings-venue-assign">
                          <legend className="echat-field__label">Доступ к заведениям</legend>
                          <div className="market-checkbox-grid">
                            {venues.map((venue) => (
                              <label key={venue.id} className="market-filters__checkbox">
                                <input
                                  type="checkbox"
                                  checked={editingVenueIds.includes(venue.id)}
                                  onChange={() => toggleEditingVenue(venue.id)}
                                />
                                <span>{venue.name}</span>
                              </label>
                            ))}
                          </div>
                        </fieldset>
                      ) : null}
                    </form>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
          <form className="market-settings-invite" onSubmit={(event) => void handleInvite(event)}>
            <div className="market-settings-invite__row">
              <label className="echat-field market-settings-invite__field">
                <span className="echat-field__label">Корпоративная почта</span>
                <input
                  className="echat-field__input"
                  type="email"
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  placeholder="colleague@company.ru"
                  required
                />
              </label>
              <SelectCombobox
                className="market-settings-invite__field"
                label="Роль в команде"
                value={invitePreset}
                options={JOB_TITLE_PRESETS.map((preset) => ({
                  value: preset.value,
                  label: preset.label,
                }))}
                onChange={(next) =>
                  setInvitePreset(next as (typeof JOB_TITLE_PRESETS)[number]['value'])
                }
              />
              <button type="submit" className="echat-btn echat-btn--primary echat-btn--sm">
                Пригласить
              </button>
            </div>
            {venues.length > 0 ? (
              <fieldset className="market-fieldset market-settings-venue-assign">
                <legend className="echat-field__label">Доступ к заведениям</legend>
                <p className="echat-muted">Сотрудник увидит только выбранные точки. Не больше 2 человек на заведение.</p>
                <div className="market-checkbox-grid">
                  {venues.map((venue) => (
                    <label key={venue.id} className="market-filters__checkbox">
                      <input
                        type="checkbox"
                        checked={inviteVenueIds.includes(venue.id)}
                        onChange={() => toggleInviteVenue(venue.id)}
                      />
                      <span>{venue.name}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            ) : null}
          </form>
        </>
      )}

      <PaywallModal
        open={seatLimitPaywall != null}
        title="Лимит команды"
        message={seatLimitPaywall?.message ?? ''}
        onDismiss={() => setSeatLimitPaywall(null)}
        onUpgrade={() => {
          setSeatLimitPaywall(null)
          navigate('/payments')
        }}
      />
      {ownerPaywallModal}
    </div>
  )
}
